import { useEffect, useState } from "react";
import useDialog from "../../../utils/hooks/useDialog";
import { useTranslation } from "react-i18next";
import {
  DIRECTION,
  DISPLAY_STATUS,
  OBSERVATION_STATUS,
  OBSERVATION_TYPE,
  displayStatusMeta,
} from "../../../utils/constants/observations";
import Avatar from "../../../components/Avatar/Avatar";
import { notBeforeToday, toLocalISODate } from "../../../utils/helpers/dateDisplay";
import PeriodPicker, { lessonFields } from "../../../components/scheduling/PeriodPicker";
import FocusStandardChips, {
  visitFocusStandards,
} from "../../../components/scheduling/FocusStandardChips";

export default function ObservationDetailModal({
  observation,
  rubric = null,
  onClose,
  onConfirm,
  onDecline,
  onCancel,
  onReschedule,
}) {
  const { t } = useTranslation();
  const todayISO = toLocalISODate(new Date());
  const isOutgoing = observation.direction === DIRECTION.OUTGOING;
  const peerUuid = observation.observed_teacher?.uuid || observation.counterparty?.uuid;

  const [view, setView] = useState("details"); // "details" | "reschedule"
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  // Keeps the booked day when it is still ahead, and falls to today when it is
  // not — a visit reached here after its date has passed would otherwise open
  // the picker below its own `min`.
  const [reschedDate, setReschedDate] = useState(notBeforeToday(observation.scheduled_date));
  const [reschedPeriod, setReschedPeriod] = useState(null);
  const [reschedLesson, setReschedLesson] = useState(null);
  const [busy, setBusy] = useState(false);

  // ESC peels one layer: reschedule → details, cancel-confirm → details,
  // details → close. Keeps typed input from being discarded wholesale.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (view === "reschedule") setView("details");
      else if (confirmingCancel) setConfirmingCancel(false);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, confirmingCancel, onClose]);

  // Focus trap + scroll lock + focus restore. Escape is handled above (it peels
  // internal layers), so this hook doesn't bind it.
  const dialogRef = useDialog(onClose, { escapeCloses: false });

  const statusLabel = t(displayStatusMeta(observation.displayStatus).labelKey);
  const periodText = observation.period
    ? `${t("upcoming.period", { period: observation.period })}${observation.group?.name ? ` (${observation.group.name})` : ""}`
    : "—";
  const focusStandards = visitFocusStandards(
    rubric,
    observation.focus_standards,
    observation.domain_subject?.uuid,
  );
  const hasFocus = focusStandards.length > 0;
  // "No focus" only for a booking that asked for none. Focus standards that do
  // not resolve here (booked on an earlier rubric version, or not scored on the
  // lesson subject) leave the focus line out rather than claim there are none.
  const showFocus = hasFocus || !(observation.focus_standards || []).length;
  const isPendingIncomingPeer =
    observation.direction === DIRECTION.INCOMING &&
    observation.type === OBSERVATION_TYPE.PEER &&
    observation.displayStatus === DISPLAY_STATUS.PENDING;
  // The only touch-reachable way to back out of an already-confirmed visit.
  const isConfirmedIncomingPeer =
    observation.direction === DIRECTION.INCOMING &&
    observation.type === OBSERVATION_TYPE.PEER &&
    observation.displayStatus === DISPLAY_STATUS.CONFIRMED;
  const isDeclinedIncomingPeer =
    observation.direction === DIRECTION.INCOMING &&
    observation.type === OBSERVATION_TYPE.PEER &&
    observation.displayStatus === DISPLAY_STATUS.DECLINED;
  // Cancel/reschedule are the requester's controls on a live outgoing visit.
  const canManageOwnVisit =
    isOutgoing && observation.status === OBSERVATION_STATUS.SCHEDULED;

  const statusBadge = displayStatusMeta(observation.displayStatus).badgeOnDark;

  const submitReschedule = async () => {
    if (!reschedDate || !reschedPeriod) return;
    setBusy(true);
    await onReschedule(observation.uuid, {
      scheduled_date: reschedDate,
      period: reschedPeriod,
      ...lessonFields(reschedLesson),
    });
    setBusy(false);
  };

  // Mirrors submitReschedule: guard against a double-submit while the cancel
  // request is in flight.
  const submitCancel = async () => {
    if (busy) return;
    setBusy(true);
    await onCancel(observation.uuid, cancelReason);
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="obs-detail-title"
        tabIndex={-1}
        className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`p-8 ${isOutgoing ? "bg-indigo-600" : "bg-slate-900"} text-white relative`}
        >
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
            aria-label={t("common.close")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-widest opacity-60 mb-1">
                {observation.type === OBSERVATION_TYPE.OFFICIAL
                  ? t("upcoming.officialObservation")
                  : t("upcoming.peerVisit")}
              </p>
              <h3 id="obs-detail-title" className="text-2xl font-black">{observation.dateLabel}</h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/20">
              {periodText}
            </span>
            <span
              className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${statusBadge}`}
            >
              {statusLabel}
            </span>
          </div>
        </div>

        {view === "reschedule" ? (
          <div className="p-8 space-y-6">
            <h4 className="text-lg font-black text-slate-900">{t("upcoming.rescheduleTitle")}</h4>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("upcoming.visitDate")}
              </label>
              <input
                type="date"
                value={reschedDate}
                min={todayISO}
                onChange={(e) => {
                  setReschedDate(e.target.value);
                  setReschedPeriod(null);
                  setReschedLesson(null);
                }}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("upcoming.pickLessonLabel")}
              </label>
              <PeriodPicker
                teacherUuid={peerUuid}
                date={reschedDate}
                value={reschedPeriod}
                observerUuid={observation.main_observer?.uuid}
                observationUuid={observation.uuid}
                onChange={(p, lesson) => {
                  setReschedPeriod(p);
                  setReschedLesson(lesson);
                }}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setView("details")}
                disabled={busy}
                className="flex-1 py-3.5 bg-slate-100 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 disabled:opacity-60"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={submitReschedule}
                disabled={!reschedDate || !reschedPeriod || busy}
                className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                  !reschedDate || !reschedPeriod || busy
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200"
                }`}
              >
                {t("upcoming.saveChanges")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 space-y-8">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                  {isOutgoing ? t("upcoming.personToObserve") : t("upcoming.roleObserver")}
                </p>
                <div className="flex items-center gap-3">
                  {observation.counterparty ? (
                    <Avatar
                      user={observation.counterparty}
                      className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 font-black"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 font-black">
                      —
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-black text-slate-900">{observation.counterpartyName}</p>
                    <p className="text-[10px] font-bold text-slate-500">
                      {observation.type === OBSERVATION_TYPE.OFFICIAL
                        ? t("upcoming.roleObserver")
                        : t("upcoming.rolePeerExpert")}
                    </p>
                  </div>
                </div>
              </div>
              {(observation.domain_subject?.name || observation.group?.name) && (
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    {t("upcoming.lessonDetails")}
                  </p>
                  {observation.domain_subject?.name && (
                    <p className="text-sm font-black text-slate-900">{observation.domain_subject.name}</p>
                  )}
                  {observation.group?.name && (
                    <p className="text-[10px] font-bold text-slate-500">{observation.group.name}</p>
                  )}
                </div>
              )}
            </div>

            <div>
              {showFocus && (
                <div className="mb-3">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    {t("upcoming.focusLabel")}
                  </p>
                  {hasFocus ? (
                    <FocusStandardChips standards={focusStandards} />
                  ) : (
                    <p className="text-sm text-slate-400 italic">
                      {t("upcoming.noFocusStandards")}
                    </p>
                  )}
                </div>
              )}
              <p className="text-sm leading-relaxed">
                <span className="block text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">
                  {t("upcoming.visitNote")}
                </span>
                {observation.noteText ? (
                  <span className="text-slate-600">{observation.noteText}</span>
                ) : (
                  <span className="text-slate-400 italic">{t("upcoming.noVisitNote")}</span>
                )}
              </p>
            </div>

            {confirmingCancel ? (
              <div className="space-y-4 p-5 bg-rose-50 border border-rose-100 rounded-2xl">
                <div>
                  <p className="text-sm font-black text-rose-800">{t("upcoming.confirmCancelTitle")}</p>
                  <p className="text-[11px] font-bold text-rose-500 mt-1">{t("upcoming.confirmCancelBody")}</p>
                </div>
                <textarea
                  placeholder={t("upcoming.cancelReasonPlaceholder")}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-rose-200 rounded-xl text-sm text-slate-900 outline-none focus:ring-2 focus:ring-rose-300/30 min-h-[60px] resize-none"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmingCancel(false)}
                    disabled={busy}
                    className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-60"
                  >
                    {t("upcoming.keepVisit")}
                  </button>
                  <button
                    onClick={submitCancel}
                    disabled={busy}
                    className="flex-1 py-3 bg-rose-600 text-white rounded-xl text-sm font-semibold hover:bg-rose-700 disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {t("upcoming.cancelVisitBtn")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-4 flex gap-3">
                {isPendingIncomingPeer ? (
                  <>
                    <button
                      onClick={() => {
                        onConfirm(observation.uuid);
                        onClose();
                      }}
                      className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl text-sm font-semibold hover:bg-emerald-700 shadow-xl shadow-emerald-100"
                    >
                      {t("upcoming.confirmVisit")}
                    </button>
                    <button
                      onClick={() => {
                        onDecline(observation.uuid);
                        onClose();
                      }}
                      className="flex-1 py-4 bg-white border-2 border-rose-600 text-rose-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-rose-50"
                    >
                      {t("upcoming.decline")}
                    </button>
                  </>
                ) : canManageOwnVisit ? (
                  <>
                    <button
                      onClick={() => setView("reschedule")}
                      className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl text-sm font-semibold hover:bg-indigo-700 shadow-xl shadow-indigo-100"
                    >
                      {t("upcoming.reschedule")}
                    </button>
                    <button
                      onClick={() => setConfirmingCancel(true)}
                      className="flex-1 py-4 bg-white border-2 border-rose-600 text-rose-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-rose-50"
                    >
                      {t("upcoming.cancelVisitBtn")}
                    </button>
                  </>
                ) : isConfirmedIncomingPeer ? (
                  <>
                    <button
                      onClick={onClose}
                      className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-sm font-semibold hover:bg-slate-800 shadow-xl shadow-slate-200"
                    >
                      {t("upcoming.closeDetails")}
                    </button>
                    <button
                      onClick={() => {
                        onDecline(observation.uuid);
                        onClose();
                      }}
                      className="flex-1 py-4 bg-white border-2 border-rose-600 text-rose-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-rose-50"
                    >
                      {t("upcoming.decline")}
                    </button>
                  </>
                ) : isDeclinedIncomingPeer ? (
                  <>
                    <button
                      onClick={() => {
                        onConfirm(observation.uuid);
                        onClose();
                      }}
                      className="flex-1 py-4 bg-emerald-600 text-white rounded-2xl text-sm font-semibold hover:bg-emerald-700 shadow-xl shadow-emerald-100"
                    >
                      {t("upcoming.restore")}
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-200"
                    >
                      {t("upcoming.closeDetails")}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={onClose}
                    className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-sm font-semibold hover:bg-slate-800 shadow-xl shadow-slate-200"
                  >
                    {t("upcoming.closeDetails")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
